import { SIDEBAR_NAV, type NavItem } from '@/constants/navigation'
import { ROUTES } from '@/constants/routes'

/** Stable chrome titles — module / tab / screen labels only. Never entity names. */
const EXACT_TITLES: Record<string, string> = {
  [ROUTES.DASHBOARD]: 'Dashboard',
  [ROUTES.WORKSPACE]: 'Workspace',
  [ROUTES.NOTIFICATIONS]: 'Notifications',
  [ROUTES.ACCOUNT]: 'Settings',
  [ROUTES.MY_DEVICES]: 'My devices',
  [ROUTES.SETTINGS_SECURITY]: 'Security',
  [ROUTES.MASTER_DATA]: 'Database',
  [ROUTES.CUSTOMERS]: 'Parties',
  [ROUTES.CUSTOMERS_CREATE]: 'Parties',
  [ROUTES.SUPPLIERS]: 'Vendors',
  [ROUTES.SUPPLIERS_CREATE]: 'Vendors',
  [ROUTES.SALES_AGENTS]: 'Agents',
  [ROUTES.CATEGORIES]: 'Categories',
  [ROUTES.QUALITIES]: 'Qualities',
  [ROUTES.COLOURS]: 'Colors',
  [ROUTES.WAREHOUSES]: 'Warehouses',
  [ROUTES.PRODUCTS]: 'Designs',
  [ROUTES.SALES_LEADS_CREATE]: 'Queries',
  [ROUTES.DISPATCH_SHEET]: 'Dispatches',
  [ROUTES.DISPATCH_SHEET_CREATE]: 'Dispatches',
  [ROUTES.PURCHASE_SHEET]: 'Purchases',
  [ROUTES.PURCHASE_SHEET_CREATE]: 'Purchases',
  [ROUTES.TASKS]: 'Tasks',
  [ROUTES.TASKS_CREATE]: 'Tasks',
  [ROUTES.MESSENGER]: 'Messages',
  [ROUTES.REPORTS]: 'Reports',
  [ROUTES.REPORTS_PURCHASE_SUMMARY]: 'Purchase Summary',
  [ROUTES.REPORTS_PURCHASE_TREND]: 'Purchase Trend',
  [ROUTES.REPORTS_DISPATCH_SUMMARY]: 'Dispatch Summary',
  [ROUTES.REPORTS_DISPATCH_TREND]: 'Sales Trend',
  [ROUTES.REPORTS_EXPORTS]: 'Detail Exports',
  [ROUTES.SETTINGS]: 'Settings',
  [ROUTES.SETTINGS_SHEETS_SYNC]: 'Sheets Sync',
  [ROUTES.USERS]: 'Users',
  [ROUTES.USERS_CREATE]: 'Users',
  [ROUTES.PERMISSIONS]: 'Permissions',
  [ROUTES.USERS_AGENT_LINKS]: 'Agent links',
  [ROUTES.QUOTATIONS]: 'Quotations',
  [ROUTES.QUOTATIONS_CREATE]: 'Quotations',
  [ROUTES.ESTIMATES]: 'Estimates',
  [ROUTES.ESTIMATES_CREATE]: 'Estimates',
  [ROUTES.COMPANY_BANK_ACCOUNTS]: 'Bank accounts',
  [ROUTES.QUOTATION_TERMS_TEMPLATES]: 'Terms templates',
  [ROUTES.CATALOG]: 'Catalog',
  [ROUTES.CATALOG_CREATE]: 'Catalog',
}

const PATTERN_TITLES: { test: (path: string) => boolean; title: string }[] = [
  {
    test: (p) => /\/master-data\/customers\//.test(p),
    title: 'Parties',
  },
  {
    test: (p) => /\/master-data\/suppliers\//.test(p),
    title: 'Vendors',
  },
  {
    test: (p) => /\/master-data\/categories\//.test(p),
    title: 'Categories',
  },
  {
    test: (p) => /\/sales\/quotations\//.test(p),
    title: 'Quotations',
  },
  {
    test: (p) => /\/sales\/estimates\//.test(p),
    title: 'Estimates',
  },
  {
    test: (p) => /^\/catalog\//.test(p),
    title: 'Catalog',
  },
  {
    test: (p) => /\/master-data\/products\//.test(p),
    title: 'Designs',
  },
  {
    test: (p) => /\/dispatch\/sheet\//.test(p),
    title: 'Dispatches',
  },
  {
    test: (p) => /\/purchases\/sheet\//.test(p),
    title: 'Purchases',
  },
  {
    test: (p) => /\/tasks\//.test(p) && p !== ROUTES.TASKS_CREATE,
    title: 'Tasks',
  },
  {
    // Same "section name, not the entity's own name" rule as Tasks above —
    // the open chat's own name is already shown inside the thread's own
    // header (MessengerThread), so the app chrome stays "Messages".
    test: (p) => /\/messenger\//.test(p),
    title: 'Messages',
  },
  {
    test: (p) => /\/users\//.test(p) && p !== ROUTES.USERS_CREATE,
    title: 'Users',
  },
]

/** Leads nav parent stays “Leads”; chrome title follows Queries / Registered / Follow up. */
function resolveSalesLeadsTitle(pathname: string, search: string): string | null {
  if (pathname === ROUTES.SALES_LEADS) {
    const tab = new URLSearchParams(search).get('tab')
    if (tab === 'registered') return 'Registered'
    if (tab === 'follow') return 'Follow up'
    if (tab === 'summary') return 'Summary'
    if (tab === 'old') return 'Old customers'
    if (tab === 'inbox') return 'Inbox'
    return 'Queries'
  }
  if (!pathname.startsWith(`${ROUTES.SALES_LEADS}/`)) return null
  if (pathname.startsWith(`${ROUTES.SALES_LEADS}/old`)) return null
  if (pathname.startsWith(`${ROUTES.SALES_LEADS}/inbox`)) return 'Inbox'
  if (pathname === ROUTES.SALES_LEADS_CREATE || pathname.endsWith('/register')) {
    return 'Queries'
  }
  return new URLSearchParams(search).get('view') === 'registered' ? 'Registered' : 'Queries'
}

function titleFromNav(pathname: string): string | undefined {
  const walk = (items: NavItem[]) => {
    for (const item of items) {
      if (item.href === pathname) return item.label
    }
    return undefined
  }

  for (const entry of SIDEBAR_NAV) {
    if (entry.type === 'item' && entry.href === pathname) return entry.label
    if (entry.type === 'group') {
      const match = walk(entry.children)
      if (match) return match
    }
  }
  return undefined
}

/**
 * Module / tab title for the global header. Prefer this over PageLayout `title`
 * so detail screens never push entity names (company, person, bill no.) into chrome.
 * Pass `search` so Leads list/detail can switch Queries ↔ Registered.
 */
export function resolvePageTitleFromPath(pathname: string, search = ''): string | null {
  const salesTitle = resolveSalesLeadsTitle(pathname, search)
  if (salesTitle) return salesTitle

  if (EXACT_TITLES[pathname]) return EXACT_TITLES[pathname]

  for (const rule of PATTERN_TITLES) {
    if (rule.test(pathname)) return rule.title
  }

  return titleFromNav(pathname) ?? null
}
